import { onlyDigits } from './documents';

/** Address fields a CEP lookup can resolve. Number and complement are always typed by hand. */
export interface CepResult {
  state: string;
  city: string;
  district: string;
  street: string;
}

const LOOKUP_TIMEOUT_MS = 2500;

/** Fetch with an abort-based timeout so a slow provider never blocks the fallback chain. */
async function fetchJson(url: string): Promise<unknown> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } finally {
    clearTimeout(timer);
  }
}

/** Primary provider — fastest. Throws on any failure so the caller falls back to ViaCEP. */
async function fromBrasilApi(cep: string): Promise<CepResult> {
  const data = (await fetchJson(`https://brasilapi.com.br/api/cep/v1/${cep}`)) as {
    state?: string;
    city?: string;
    neighborhood?: string;
    street?: string;
  };
  return {
    state: data.state ?? '',
    city: data.city ?? '',
    district: data.neighborhood ?? '',
    street: data.street ?? '',
  };
}

/** Fallback provider. ViaCEP answers HTTP 200 with `{ erro: true }` for unknown CEPs. */
async function fromViaCep(cep: string): Promise<CepResult> {
  const data = (await fetchJson(`https://viacep.com.br/ws/${cep}/json/`)) as {
    erro?: boolean;
    uf?: string;
    localidade?: string;
    bairro?: string;
    logradouro?: string;
  };
  if (data.erro) throw new Error('CEP not found');
  return {
    state: data.uf ?? '',
    city: data.localidade ?? '',
    district: data.bairro ?? '',
    street: data.logradouro ?? '',
  };
}

/** In-memory cache of resolved lookups, keyed by the 8-digit CEP. Lives for the page session only. */
const cache = new Map<string, CepResult>();

/**
 * Resolve a CEP into address fields, entirely client-side.
 *
 * Tries BrasilAPI first, falls back to ViaCEP, and returns `null` when both fail
 * (or the input is not 8 digits) so the caller can release free typing. Successful
 * lookups are cached for the session so re-typing the same CEP skips the network.
 */
export async function lookupCep(cepMasked: string): Promise<CepResult | null> {
  const cep = onlyDigits(cepMasked);
  if (cep.length !== 8) return null;

  const cached = cache.get(cep);
  if (cached) return cached;

  let result: CepResult | null;
  try {
    result = await fromBrasilApi(cep);
  } catch {
    try {
      result = await fromViaCep(cep);
    } catch {
      return null;
    }
  }

  cache.set(cep, result);
  return result;
}
